import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { isFeatureEnabled } from '@michelin/central-provider';

export type UIVersion = 'new' | 'legacy';

interface UIVersionContextValue {
  uiVersion: UIVersion;
  toggleUIVersion: () => void;
  isNewUI: boolean;
  featureEnabled: boolean;
}

const UIVersionContext = createContext<UIVersionContextValue>({
  uiVersion: 'legacy',
  toggleUIVersion: () => {},
  isNewUI: false,
  featureEnabled: false,
});

const STORAGE_KEY = 'cp-ui-version';

export function UIVersionProvider({ children }: { children: React.ReactNode }) {
  const featureEnabled = isFeatureEnabled('msp3_new_interface');

  const [uiVersion, setUIVersion] = useState<UIVersion>(() => {
    if (!featureEnabled) return 'legacy';
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'new' ? 'new' : 'legacy';
    } catch {
      return 'legacy';
    }
  });

  const toggleUIVersion = useCallback(() => {
    if (!featureEnabled) return;
    setUIVersion((prev) => {
      const next = prev === 'legacy' ? 'new' : 'legacy';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, [featureEnabled]);

  const isNewUI = featureEnabled && uiVersion === 'new';

  const value = useMemo(
    () => ({
      uiVersion,
      toggleUIVersion,
      isNewUI,
      featureEnabled,
    }),
    [uiVersion, toggleUIVersion, isNewUI, featureEnabled],
  );

  return <UIVersionContext.Provider value={value}>{children}</UIVersionContext.Provider>;
}

export function useUIVersion() {
  return useContext(UIVersionContext);
}
